// theme.ts — the shared "button material" + color helpers that lift every surface from flat
// stroked-rectangle (pre-art) to a premium hybrid-casual button: a rounded 2-stop gradient body,
// a top gloss line, a drop shadow, and a rim light. One primitive used by ui.ts, the menu CTAs,
// the in-game action dock, and the results buttons so the whole product reads as one designed kit.
import Phaser from 'phaser';

/** Lighten (amt>0) or darken (amt<0) a 0xRRGGBB color by a 0..1 fraction. */
export function shade(color: number, amt: number): number {
  let r = (color >> 16) & 0xff;
  let g = (color >> 8) & 0xff;
  let b = color & 0xff;
  if (amt >= 0) {
    r += (255 - r) * amt;
    g += (255 - g) * amt;
    b += (255 - b) * amt;
  } else {
    r *= 1 + amt;
    g *= 1 + amt;
    b *= 1 + amt;
  }
  return (Math.round(r) << 16) | (Math.round(g) << 8) | Math.round(b);
}

export interface ButtonPaint {
  top?: number;
  bottom?: number;
  radius?: number;
  glossy?: boolean;
  pressed?: boolean;
  border?: number;
}

/**
 * Paint a premium button background into a Graphics (cleared first), centered at (0,0): drop shadow
 * → gradient body → top gloss → rim light. `base` is the button's signature color; the gradient is
 * derived from it unless top/bottom are given. `pressed` darkens + sinks it for tactile feedback.
 */
export function paintButtonBg(
  g: Phaser.GameObjects.Graphics,
  w: number,
  h: number,
  base: number,
  opts: ButtonPaint = {},
): void {
  // Tactical OLED Arcade material (style-bible v2): flat-ish fill with a crisp 1–2px top highlight
  // bevel and a thin rim — disciplined, not glossy. Subtler gradient than the old premium look.
  // Flat tactical token: a barely-there gradient + a thin bevel, not a glossy 3D pill.
  const lift = opts.pressed ? -0.05 : 0.05;
  const top = opts.top ?? shade(base, lift);
  const bottom = opts.bottom ?? shade(base, opts.pressed ? -0.2 : -0.04);
  const r = opts.radius ?? Math.min(h * 0.3, 14);
  const sink = opts.pressed ? 1 : 0;
  g.clear();
  // soft drop shadow (smaller/closer when pressed)
  g.fillStyle(0x000000, opts.pressed ? 0.14 : 0.18);
  g.fillRoundedRect(-w / 2 + 1.5, -h / 2 + (opts.pressed ? 2 : 4), w, h, r);
  // body. `glossy:false` is the flat Tactical OLED path used by primary CTAs; avoid Phaser's gradient
  // fill there because it can alias as a broken left-edge band on large rounded buttons.
  if (opts.glossy === false) g.fillStyle(opts.pressed ? bottom : base, 1);
  else g.fillGradientStyle(top, top, bottom, bottom, 1);
  g.fillRoundedRect(-w / 2, -h / 2 + sink, w, h, r);
  // crisp top-highlight bevel (inset 0 1px 0 #fff @ ~22%)
  if (opts.glossy !== false && !opts.pressed) {
    g.fillStyle(0xffffff, 0.09);
    g.fillRoundedRect(-w / 2 + 2, -h / 2 + 1.5, w - 4, Math.max(1.5, h * 0.08), {
      tl: Math.max(1, r - 1),
      tr: Math.max(1, r - 1),
      bl: 0,
      br: 0,
    });
  }
  // thin rim
  g.lineStyle(1.25, opts.border ?? 0xffffff, 0.12);
  g.strokeRoundedRect(-w / 2, -h / 2 + sink, w, h, r);
}

/**
 * A premium themed button: painted material + label, with hover-brighten and press-sink feedback.
 * Returns the container (so callers can position/depth). `base` drives the gradient; `ink` is the
 * label color — pick ink for >=4.5:1 against `base`.
 */
export function themedButton(
  scene: Phaser.Scene,
  x: number,
  y: number,
  label: string,
  base: number,
  ink: number,
  onClick: () => void,
  opts: { width: number; height: number; fontFamily: string; fontPx: number; glow?: number } ,
): Phaser.GameObjects.Container {
  const { width: w, height: h } = opts;
  const c = scene.add.container(x, y);
  if (opts.glow !== undefined) {
    const glow = scene.add.graphics();
    glow.fillStyle(opts.glow, 0.1);
    glow.fillRoundedRect(-w / 2 - 2, -h / 2 - 1, w + 4, h + 5, Math.min(h * 0.4, 14));
    c.add(glow);
  }
  const g = scene.add.graphics();
  paintButtonBg(g, w, h, base, {});
  const txt = scene.add
    .text(0, 0, label, { fontFamily: opts.fontFamily, fontSize: `${opts.fontPx}px`, color: cssHex(ink), fontStyle: 'bold' })
    .setOrigin(0.5);
  const hit = scene.add.rectangle(0, 0, w, h, 0xffffff, 0.001).setInteractive({ useHandCursor: true });
  c.add([g, txt, hit]);
  hit.on('pointerover', () => paintButtonBg(g, w, h, shade(base, 0.08), {}));
  hit.on('pointerout', () => paintButtonBg(g, w, h, base, {}));
  hit.on('pointerdown', () => {
    paintButtonBg(g, w, h, base, { pressed: true });
    txt.setY(1);
  });
  hit.on('pointerup', () => {
    paintButtonBg(g, w, h, base, {});
    txt.setY(0);
    onClick();
  });
  return c;
}

function cssHex(color: number): string {
  return '#' + color.toString(16).padStart(6, '0');
}
